/**
 * Theme Context and Provider
 *
 * Centralized theme management for all Tonnie applications.
 * Supports light/dark/system/custom modes with organization theming.
 *
 * @example
 * ```tsx
 * // In your app root
 * import { ThemeProvider } from '@tonnie/design-tokens/theme';
 *
 * function App() {
 *   return (
 *     <ThemeProvider appId="journey" enableSystem enableCustom>
 *       <AppContent />
 *     </ThemeProvider>
 *   );
 * }
 * ```
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';

import type {
  Theme,
  ThemeMode,
  IOrganizationTheme,
  IThemeContextValue,
  IThemeProviderProps,
  IStorageAdapter,
  IStorageKeys,
} from './types';

import {
  createStorageKeys,
  webStorageAdapter,
  loadThemeMode,
  saveThemeMode,
  loadOrganizationTheme,
  saveOrganizationTheme,
} from './storage';

import {
  getSystemTheme,
  subscribeToSystemTheme,
  applyThemeClass,
  applyCustomColors,
  applyCustomAssets,
  updateColorSchemeMeta,
} from './css-utils';

// ============================================================================
// CONTEXT
// ============================================================================

const ThemeContext = createContext<IThemeContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function ThemeProvider({
  children,
  appId,
  defaultTheme = 'light',
  switchable = true,
  enableSystem = true,
  enableCustom = true,
  initialOrganizationTheme,
  storageAdapter,
}: IThemeProviderProps) {
  // Refs for stable references
  const adapterRef = useRef<IStorageAdapter>(storageAdapter || webStorageAdapter);
  const keysRef = useRef<IStorageKeys>(createStorageKeys(appId));

  // State
  const [isInitialized, setIsInitialized] = useState(false);
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    // Use defaultTheme as initial mode; enableSystem only gates availability
    return defaultTheme;
  });
  const [organizationTheme, setOrganizationThemeState] = useState<IOrganizationTheme | undefined>(
    initialOrganizationTheme
  );

  // Compute resolved theme
  const theme = useMemo<Theme>(() => {
    if (themeMode === 'system') {
      return getSystemTheme();
    }
    if (themeMode === 'custom') {
      // Custom mode uses light as base
      return 'light';
    }
    return themeMode as Theme;
  }, [themeMode]);

  // Derived states
  const isDark = theme === 'dark';
  const isLight = theme === 'light';

  // Load stored preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      const adapter = adapterRef.current;
      const keys = keysRef.current;

      // Use defaultTheme as fallback; enableSystem only gates availability
      const defaultMode: ThemeMode = defaultTheme;

      // Load theme mode
      const storedMode = await loadThemeMode(adapter, keys, defaultMode);

      // Validate mode is allowed by config
      let validMode = storedMode;
      if (storedMode === 'system' && !enableSystem) {
        validMode = defaultTheme;
      }
      if (storedMode === 'custom' && !enableCustom) {
        validMode = defaultTheme;
      }

      setThemeModeState(validMode);

      // Load organization theme if custom mode is enabled
      if (enableCustom && !initialOrganizationTheme) {
        const storedOrgTheme = await loadOrganizationTheme(adapter, keys);
        if (storedOrgTheme) {
          setOrganizationThemeState(storedOrgTheme);
        }
      }

      setIsInitialized(true);
    };

    loadPreferences();
  }, [defaultTheme, enableSystem, enableCustom, initialOrganizationTheme]);

  // Set theme mode with persistence
  const setThemeMode = useCallback(
    (mode: ThemeMode) => {
      // Validate mode is allowed
      if (mode === 'system' && !enableSystem) {
        console.warn('[ThemeProvider] System mode is not enabled');
        return;
      }
      if (mode === 'custom' && !enableCustom) {
        console.warn('[ThemeProvider] Custom mode is not enabled');
        return;
      }

      setThemeModeState(mode);
      saveThemeMode(adapterRef.current, keysRef.current, mode);

      // Clear custom colors when switching away from custom mode
      if (mode !== 'custom') {
        applyCustomColors(undefined);
      }
    },
    [enableSystem, enableCustom]
  );

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    if (!switchable) return;

    // If in system mode, switch to the opposite of current resolved theme
    const newMode = theme === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
  }, [switchable, theme, setThemeMode]);

  // Set organization theme
  const setOrganizationTheme = useCallback(
    (newTheme: IOrganizationTheme | undefined) => {
      if (!enableCustom) {
        console.warn('[ThemeProvider] Custom mode is not enabled');
        return;
      }

      setOrganizationThemeState(newTheme);
      saveOrganizationTheme(adapterRef.current, keysRef.current, newTheme);
    },
    [enableCustom]
  );

  // Apply theme class when theme changes
  useEffect(() => {
    if (!isInitialized) return;
    applyThemeClass(theme);
    updateColorSchemeMeta(theme);
  }, [theme, isInitialized]);

  // Apply custom colors when in custom mode
  useEffect(() => {
    if (!isInitialized) return;

    if (themeMode === 'custom' && organizationTheme?.colors) {
      applyCustomColors(organizationTheme.colors);
    } else if (themeMode !== 'custom') {
      applyCustomColors(undefined);
    }
  }, [themeMode, organizationTheme, isInitialized]);

  // Apply custom assets when organization theme changes
  useEffect(() => {
    if (!isInitialized) return;

    if (organizationTheme?.assets) {
      applyCustomAssets(organizationTheme.assets);
    }
  }, [organizationTheme, isInitialized]);

  // Subscribe to system theme changes
  useEffect(() => {
    if (themeMode !== 'system') return;

    return subscribeToSystemTheme((newTheme) => {
      applyThemeClass(newTheme);
      updateColorSchemeMeta(newTheme);
    });
  }, [themeMode]);

  // Context value
  const value = useMemo<IThemeContextValue>(
    () => ({
      theme,
      themeMode,
      setThemeMode,
      toggleTheme,
      switchable,
      isDark,
      isLight,
      isInitialized,
      organizationTheme,
      setOrganizationTheme,
    }),
    [
      theme,
      themeMode,
      setThemeMode,
      toggleTheme,
      switchable,
      isDark,
      isLight,
      isInitialized,
      organizationTheme,
      setOrganizationTheme,
    ]
  );

  // Apply initial theme class before first render to prevent flash
  useEffect(() => {
    applyThemeClass(theme);
  }, []);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access theme context
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, toggleTheme, isDark } = useTheme();
 *
 *   return (
 *     <button onClick={toggleTheme}>
 *       {isDark ? 'Switch to Light' : 'Switch to Dark'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useTheme(): IThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Hook to access just the theme mode (lighter weight)
 */
export function useThemeMode(): ThemeMode {
  const { themeMode } = useTheme();
  return themeMode;
}

/**
 * Hook to check if dark mode is active
 */
export function useIsDark(): boolean {
  const { isDark } = useTheme();
  return isDark;
}

// ============================================================================
// EXPORTS
// ============================================================================

export { ThemeContext };
